import { useCallback, useEffect, useState } from "react";
import { api } from "@/lib/api";
import { PiggyBank, Sparkle, TrendUp, BellSimpleRinging } from "@phosphor-icons/react";

const money = (n) => `$${(Number(n) || 0).toLocaleString("en-US", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;

export function SavingsTracker({ businessId, refreshKey = 0 }) {
  const [data, setData] = useState(null);

  const load = useCallback(() => {
    api.get(`/businesses/${businessId}/savings`).then((r) => setData(r.data)).catch(() => setData(null));
  }, [businessId]);

  useEffect(() => { load(); }, [load, refreshKey]);

  if (!data) return null;
  const hasAnything = data.total_saved > 0 || (data.hikes_caught || []).length > 0 || (data.cancelled_pending || []).length > 0;
  if (!hasAnything) return null;

  return (
    <div className="bk-card p-6" data-testid="savings-tracker">
      <div className="flex flex-wrap items-start justify-between gap-4">
        <div>
          <h3 className="font-display text-xl font-bold flex items-center gap-2" style={{ color: "var(--bk-text)" }}>
            <PiggyBank size={20} weight="duotone" color="var(--bk-success)" /> Rowan&apos;s savings tracker
          </h3>
          <p className="text-sm mt-1 flex items-start gap-1.5" style={{ color: "var(--bk-text-2)" }} data-testid="savings-rowan-message">
            <Sparkle size={14} weight="fill" color="var(--bk-gold)" className="shrink-0 mt-0.5" /> {data.rowan_message}
          </p>
        </div>
        <div className="flex gap-2">
          <div className="rounded-2xl border px-4 py-2.5 text-center" style={{ borderColor: "rgba(91,138,111,0.3)", background: "var(--bk-success-bg)" }} data-testid="savings-total">
            <p className="text-[10px] uppercase tracking-[0.14em] font-semibold" style={{ color: "var(--bk-success)" }}>Saved so far</p>
            <p className="font-display text-lg font-bold" style={{ color: "var(--bk-success)" }}>{money(data.total_saved)}</p>
          </div>
          {data.hikes_extra_monthly > 0 && (
            <div className="rounded-2xl border px-4 py-2.5 text-center" style={{ borderColor: "rgba(232,168,56,0.35)", background: "#FFF4D9" }} data-testid="savings-hikes-total">
              <p className="text-[10px] uppercase tracking-[0.14em] font-semibold" style={{ color: "#8A6A3B" }}>Hikes caught</p>
              <p className="font-display text-lg font-bold" style={{ color: "#8A6A3B" }}>{money(data.hikes_extra_monthly)}/mo</p>
            </div>
          )}
        </div>
      </div>

      <div className="mt-4 grid sm:grid-cols-2 gap-3">
        {(data.cancelled_stopped.length > 0 || data.cancelled_pending.length > 0) && (
          <div className="rounded-[20px] border p-4" style={{ borderColor: "var(--bk-border)" }} data-testid="savings-cancelled-panel">
            <p className="text-xs uppercase tracking-[0.14em] font-semibold mb-2" style={{ color: "var(--bk-text-2)" }}>Cancelled subscriptions</p>
            <div className="space-y-2">
              {data.cancelled_stopped.map((c) => (
                <div key={c.payee_key} className="flex items-center justify-between gap-2 text-sm" data-testid={`savings-stopped-${c.payee_key.replace(/\s+/g, "-")}`}>
                  <span style={{ color: "var(--bk-text)" }}>{c.name} <span className="text-xs" style={{ color: "var(--bk-text-2)" }}>· stopped {c.stopped_since}</span></span>
                  <span className="font-bold" style={{ color: "var(--bk-success)" }}>+{money(c.saved_so_far)}</span>
                </div>
              ))}
              {data.cancelled_pending.map((c) => (
                <div key={c.payee_key} className="flex items-center justify-between gap-2 text-sm" data-testid={`savings-pending-${c.payee_key.replace(/\s+/g, "-")}`}>
                  <span style={{ color: "var(--bk-text)" }} className="inline-flex items-center gap-1.5">
                    <BellSimpleRinging size={12} weight="fill" color="#B23B1F" /> {c.name}
                  </span>
                  <span className="text-xs font-medium" style={{ color: "#B23B1F" }}>still charging {money(c.monthly_cost)}/mo</span>
                </div>
              ))}
            </div>
          </div>
        )}
        {(data.hikes_caught || []).length > 0 && (
          <div className="rounded-[20px] border p-4" style={{ borderColor: "var(--bk-border)" }} data-testid="savings-hikes-panel">
            <p className="text-xs uppercase tracking-[0.14em] font-semibold mb-2" style={{ color: "var(--bk-text-2)" }}>Price hikes caught</p>
            <div className="space-y-2">
              {data.hikes_caught.map((h) => (
                <div key={`${h.payee_key}-${h.hike_date}`} className="flex items-center justify-between gap-2 text-sm" data-testid={`savings-hike-${h.payee_key.replace(/\s+/g, "-")}`}>
                  <span className="inline-flex items-center gap-1.5" style={{ color: "var(--bk-text)" }}>
                    <TrendUp size={12} weight="bold" color="#8A6A3B" /> {h.name} <span className="text-xs" style={{ color: "var(--bk-text-2)" }}>· {h.hike_date}</span>
                  </span>
                  <span className="text-xs font-bold" style={{ color: "#8A6A3B" }}>{money(h.previous)} → {money(h.current)} (+{h.pct}%)</span>
                </div>
              ))}
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
